import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import styles from './p170-store-overview-page.module.css';
import { useStores, UseStoresProps } from '~/features/stores';
import StoresList from './stores-list/stores-list.component';
import GoogleMapsStores from './google-maps-stores/google-maps-stores.component';
import { useDebounce } from 'react-use';
import { InputField, Typography } from '~/shared/components';
import Search from '$icons/search.svg';
import { useTranslation } from '~/shared/utils';
import USPList from './usp-list/usp-list.component';
import { usePage } from '../../hooks/usePage';
import {
    P170StoreOverviewPage as P170StoreOverviewPageType,
    StoreSearchViewModel,
} from '~/lib/data-contract';
import NavigationIcon from '$icons/navigation.svg';
import { Container } from '~/templates/blocks/components/container/container.component';

const defaultSearchProps: UseStoresProps = {
    searchParams: { Radius: 1000000, ZipCode: '0' },
};

export function getOpeningHourForToday(store: StoreSearchViewModel) {
    // ISO standard for getDay() starts day with sunday = 0, but the data we get has monday = 0, so we shift one backwards.
    const day = (new Date().getDay() + 6) % 7;
    return store.openingHours?.find((o) => (o.weekDay || 0) === day);
}

const P170StoreOverviewPage: FC = memo(() => {
    const [searchTerm, setSearchTerm] = useState('');
    const [myLocationIsUsed, setMyLocationIsUsed] = useState(false);
    const [searchProps, setSearchProps] = useState(defaultSearchProps);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [selectedStore, setSelectedStore] = useState<StoreSearchViewModel | null>(null);

    const { uspList } = usePage<P170StoreOverviewPageType>();

    const { translate } = useTranslation();

    useDebounce(
        () => {
            setDebouncedSearchTerm(searchTerm);
        },
        500,
        [searchTerm]
    );

    useEffect(() => {
        setSelectedStore(null); // Reset selected store when search term changes
        if (debouncedSearchTerm?.length < 2) {
            setSearchProps(defaultSearchProps);
        } else {
            setSearchProps({ searchTerm: debouncedSearchTerm, searchParams: { Radius: 20 } });
        }
    }, [debouncedSearchTerm]);

    const handleUseLocationClick = useCallback(async () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                    );
                    const data = await response.json();
                    const zip = data.address?.postcode;
                    if (zip) {
                        setSearchTerm(zip);
                        setMyLocationIsUsed(true);
                    } else {
                        alert('Could not determine zip code from location');
                    }
                } catch (error) {
                    alert('Failed to fetch zip code');
                }
            },
            () => {
                alert('Unable to retrieve your location');
            }
        );
    }, [setSearchTerm]);

    const {
        data,
        // isLoading,
        isFetching,
        // isIdle
    } = useStores(searchProps);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [data?.stores?.length]);

    return (
        <Container hasGutter>
            <div className={styles.root}>
                <Typography variant="display2" as="h1">
                    {translate('stores.overview.pageHeading')}
                </Typography>
                <div className={styles.rootGrid}>
                    <div className={styles.uspsCell}>
                        <USPList items={uspList} />
                    </div>
                    <div className={styles.searchCell}>
                        <div className={styles.inputWrapper}>
                            <InputField
                                placeholder={translate('stores.overview.inputHelpText')}
                                // placeholder={translate('stores.overview.inputPlaceholder')}
                                // helpText={translate('stores.overview.inputHelpText')}
                                leadingIcon={<Search />}
                                type="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {!myLocationIsUsed && (
                                <button
                                    className={styles.locationButton}
                                    onClick={handleUseLocationClick}
                                >
                                    <NavigationIcon className={styles.locationIcon} />
                                    <Typography
                                        as="span"
                                        variant="bodyXsm"
                                        className={styles.locationText}
                                    >
                                        {translate('stores.overview.locationButtonText')}
                                    </Typography>
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={styles.mapCell}>
                        <GoogleMapsStores
                            stores={selectedStore ? [selectedStore] : data?.stores || []}
                        />
                    </div>
                    <div className={styles.listCell}>
                        <StoresList
                            isFetching={isFetching}
                            stores={data?.stores}
                            shouldSortAlphabetically={!searchTerm}
                            selectedStore={selectedStore}
                            onSelectStore={setSelectedStore}
                        />
                    </div>
                </div>
            </div>
        </Container>
    );
});

P170StoreOverviewPage.displayName = 'P170StoreOverviewPage';
export default P170StoreOverviewPage;
